/**
 * Ink Revolution API Client
 *
 * Wire Shopify Customer Account → Laravel Sanctum token, sau đó gọi mọi
 * Laravel API endpoint — TẤT CẢ traffic qua Shopify App Proxy (/apps/ink/*).
 * Browser KHÔNG bao giờ gọi Laravel direct → Laravel không cần expose HTTPS
 * public; chỉ Remix App Proxy URL cần public (qua `shopify app dev` Cloudflare
 * Quick Tunnel, hoặc staging persistent).
 *
 * Endpoints:
 *   POST /apps/ink/auth/exchange  — Remix ký HMAC server-side → Laravel /auth/exchange
 *   POST /apps/ink/auth/logout    — Remix forward Bearer → Laravel /auth/logout
 *   *    /apps/ink/api/v1/*       — Remix forward Bearer + body + query → Laravel /api/v1/*
 *
 * Boot order:
 *   1. customer-context.liquid render window.INK_API_CONFIG ở <head> + nếu đã
 *      login mà chưa có token đúng owner thì BẮN exchange ngay lúc parse
 *      (`window.__inkEarlyExchange` — chạy song song với page load)
 *   2. api-client.js load → purge token của customer/store KHÁC (xem
 *      `purgeForeignToken`) → DOMContentLoaded → chưa có token thì exchange
 *      (adopt early-exchange nếu owner khớp, xem `exchange()`)
 *   3. window.inkApi available cho mọi page JS gọi Laravel API; `_fetch` tự
 *      CHỜ exchange khi login-nhưng-chưa-token → không có 401 storm lúc boot
 *
 * Token cache: localStorage `ink_api_token` + `ink_api_token_owner`
 * ("{shopDomain}|{customerId}" — token KHÔNG dùng chéo giữa 2 user/2 store).
 * 401 → auto re-exchange + retry silent.
 */
(function () {
  'use strict';

  const TOKEN_KEY = 'ink_api_token';
  // Chủ sở hữu của token đang cache: "{shopDomain}|{customerId}".
  const OWNER_KEY = 'ink_api_token_owner';

  /** Ai là chủ token nếu exchange BÂY GIỜ. null = chưa login Shopify. */
  function ownerOf(config) {
    const id = config?.customer?.id;
    if (!id) return null;

    return (config?.shopDomain || '') + '|' + id;
  }

  /**
   * Xoá token nếu nó KHÔNG thuộc customer đang login (hoặc không ai login).
   *
   * Logout Shopify KHÔNG xoá localStorage (nút logout là link native, không qua
   * `inkApi.logout()`) nên token của user A còn lại cho user B trên cùng browser — cùng vấn
   * đề khi 2 store dùng chung origin lúc theme dev.
   *
   * Chỉ GIỮ token khi có customer đang login VÀ owner khớp customer đó; mọi trường hợp khác
   * đều xoá, gồm cả token không có owner key và token mồ côi (owner key bị xoá lẻ).
   *
   * Chạy NGAY lúc script load, KHÔNG đợi DOMContentLoaded: `collection.js` +
   * `product-detail.js` đọc thẳng `localStorage.ink_api_token`.
   */
  function purgeForeignToken(config) {
    if (!localStorage.getItem(TOKEN_KEY)) return;

    const owner = ownerOf(config);
    const storedOwner = localStorage.getItem(OWNER_KEY);

    // Không rõ chủ (token không mang owner, hoặc owner bị xoá lẻ) → bỏ:
    // không cách nào biết nó của ai nên không ai được dùng.
    if (!storedOwner) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(OWNER_KEY);

      return;
    }

    // Tab này không có ai login (guest / vừa logout). Logout Shopify là browser-wide
    // nên token trong storage cũng chết theo → bỏ.
    if (!owner) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(OWNER_KEY);

      return;
    }

    // owner !== storedOwner: KHÔNG xoá. Rất có thể tab KHÁC vừa login và ghi token của
    // họ, xoá đi là phá phiên đang sống — rồi tab đó gặp 401, exchange lại bằng identity
    // cũ của tab này và fail `CUSTOMER_MISMATCH`. Ở đây chỉ cần KHÔNG
    // dùng token đó (`_syncTokenFromStorage` bỏ qua) + đánh dấu identity tab này đã cũ.
  }

  purgeForeignToken(window.INK_API_CONFIG || {});

  class InkApiClient {
    constructor(config) {
      this.proxyBase = (config?.proxyBase || '/apps/ink').replace(/\/$/, '');
      this.apiBase = this.proxyBase + '/api/v1';
      this.shopifyCustomer = config?.customer || null;
      this.shopDomain = config?.shopDomain || '';
      this._owner = ownerOf(config);
      // Qua `_syncTokenFromStorage` (không đọc thẳng localStorage): guest hoặc
      // token không rõ chủ thì instance khởi tạo với token = null.
      this._syncTokenFromStorage();
      // Promise đang chờ exchange, dùng để collapse concurrent re-exchange
      // calls khi nhiều API request cùng nhận 401 cùng lúc.
      this._exchangePromise = null;
    }

    /**
     * @param {?string} token
     * @param {?string} owner Chủ THẬT của token do server xác nhận (`data.user
     *   .shopify_customer_id`). Bỏ trống thì lấy theo INK_API_CONFIG — chỉ đúng khi
     *   config còn khớp phiên Shopify hiện tại, nên exchange() luôn truyền vào.
     */
    setToken(token, owner = null) {
      const tokenOwner = owner || this._owner;
      this.token = token || null;

      if (token && tokenOwner) {
        this._owner = tokenOwner;
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(OWNER_KEY, tokenOwner);
      } else {
        // Không có customer → không cache gì (tránh token mồ côi không rõ của ai).
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(OWNER_KEY);
      }
    }

    /**
     * Đồng bộ token trong memory với localStorage trước MỖI request.
     *
     * `purgeForeignToken` chỉ chạy lúc load + DOMContentLoaded, mà tab sống lâu có thể gặp
     * khách login account khác ở tab khác hoặc trang khôi phục từ bfcache ⇒ `this.token` vẫn
     * là của customer cũ.
     *
     * Đọc lại localStorage mỗi request (sync + rẻ); `storage` event chỉ bắn ở tab KHÁC nên
     * không dùng được cho chính tab này.
     */
    /**
     * @param {boolean} detectStale Chỉ `true` khi gọi từ tín hiệu SAU khi trang đã load
     *   (`storage` từ tab khác · `pageshow` bfcache). Xem [[_identityStale]].
     */
    _syncTokenFromStorage(detectStale = false) {
      const stored = localStorage.getItem(TOKEN_KEY);
      const storedOwner = localStorage.getItem(OWNER_KEY);

      // Chủ trong storage KHÁC identity tab này ⇒ một trong hai đã cũ. Ai cũ phụ thuộc
      // THỜI ĐIỂM phát hiện, và đây là chỗ dễ làm sai:
      //   - Lúc BOOT: trang vừa render nên `customer` từ Liquid = phiên Shopify THẬT
      //     ⇒ storage mới là cái cũ. Không được coi tab này là stale, nếu không
      //     `exchange()` bị chặn và khách MỚI không bao giờ lấy được token — mọi trang
      //     member hỏng tới khi xoá localStorage tay. Cứ để boot exchange, `setToken`
      //     sẽ ghi đè token+owner cũ.
      //   - SAU boot (tab khác vừa login / bfcache): `customer` của tab này mới là cái
      //     cũ ⇒ đánh dấu stale để không exchange bằng identity cũ (Remix trả
      //     CUSTOMER_MISMATCH) và không xoá token của phiên đang sống.
      if (detectStale) {
        this._identityStale = !!(stored && storedOwner && this._owner && storedOwner !== this._owner);
      }

      // Cùng luật với `purgeForeignToken`: chỉ dùng token khi biết chắc nó thuộc
      // customer của phiên NÀY. Guest (`_owner` null) hoặc token không có owner →
      // KHÔNG adopt, kể cả khi localStorage còn token (của user trước chưa được
      // purge) — để guest rơi xuống `this.token = stored` là gửi request kèm token
      // của người khác.
      if (!this._owner || !storedOwner || storedOwner !== this._owner) {
        this.token = null;

        return;
      }

      this.token = stored || null;
    }

    /**
     * Xoá token khỏi localStorage CHỈ khi nó là của identity tab này.
     *
     * Nếu 401 gọi `setToken(null)` (xoá vô điều kiện) thì tab cũ làm mất token của
     * tab đang sống, và tab đó phải exchange lại.
     */
    _clearOwnToken() {
      this.token = null;
      if (this._owner && localStorage.getItem(OWNER_KEY) === this._owner) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(OWNER_KEY);
      }
    }

    /**
     * Exchange Shopify Customer Account → Laravel Sanctum token.
     * Gọi App Proxy — Remix ký HMAC server-side và forward vào Laravel.
     * Theme KHÔNG cần biết hay handle HMAC secret.
     */
    async exchange() {
      if (this._exchangePromise) return this._exchangePromise;
      if (!this.shopifyCustomer) {
        return { data: null, error: { code: 'NO_CUSTOMER', message: 'Shopify customer not logged in' } };
      }
      // Identity tab này đã cũ → body sẽ mang customer_id cũ trong khi Shopify ký phiên
      // mới ⇒ Remix trả CUSTOMER_MISMATCH. Không gọi cho đỡ rác log; `pageshow` sẽ reload.
      if (this._identityStale) {
        return { data: null, error: { code: 'IDENTITY_STALE', message: 'Trang đang giữ thông tin đăng nhập cũ, tải lại trang' } };
      }

      // Adopt request bắn sớm từ <head> (customer-context.liquid) — exchange đã chạy
      // song song với parse trang, ở đây chỉ chờ kết quả + ghi token. Chỉ nhận khi
      // owner khớp identity hiện tại; dùng đúng 1 lần (401 re-exchange sau đó phải
      // bắn request mới, không dùng lại kết quả cũ).
      const early = window.__inkEarlyExchange;
      if (early && early.owner === this._owner) {
        delete window.__inkEarlyExchange;
        this._exchangePromise = early.promise.then((result) => {
          this._applyExchangeResult(result);
          return result;
        });

        try {
          return await this._exchangePromise;
        } finally {
          this._exchangePromise = null;
        }
      }

      this._exchangePromise = (async () => {
        const url = this.proxyBase + '/auth/exchange';
        const c = this.shopifyCustomer;
        let response;
        try {
          response = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'ngrok-skip-browser-warning': '1',
            },
            body: JSON.stringify({
              customer_id: c.id,
              email: c.email,
              first_name: c.first_name ?? null,
              last_name: c.last_name ?? null,
            }),
          });
        } catch (e) {
          console.error('[InkApi] exchange network error', e);
          return { data: null, error: { code: 'NETWORK_ERROR', message: e.message } };
        }

        const result = await response.json().catch(() => ({
          data: null,
          error: { code: 'PARSE_ERROR', message: 'Invalid JSON from /auth/exchange' },
        }));

        this._applyExchangeResult(result);
        return result;
      })();

      try {
        return await this._exchangePromise;
      } finally {
        this._exchangePromise = null;
      }
    }

    /** Ghi token từ kết quả exchange (đường thường lẫn early-exchange ở <head>). */
    _applyExchangeResult(result) {
      if (result?.data?.token) {
        // Chủ token = customer mà SERVER xác nhận, không phải customer trong
        // INK_API_CONFIG (config có thể stale nếu tab mở lâu và khách đã đổi
        // account ở tab khác — khi đó Remix cấp token theo phiên Shopify thật).
        const serverCustomerId = result.data.user?.shopify_customer_id;
        const owner = serverCustomerId ? this.shopDomain + '|' + serverCustomerId : null;
        this.setToken(result.data.token, owner);
      }
    }

    /**
     * Profile customer hiện tại. Gọi qua App Proxy → Laravel /auth/me.
     */
    async me() {
      return this._fetch('GET', '/auth/me');
    }

    /**
     * Logout — revoke Laravel Sanctum token + clear localStorage.
     * Idempotent: luôn clear local kể cả khi server unreachable.
     */
    async logout() {
      const url = this.proxyBase + '/auth/logout';
      const token = this.token;
      try {
        await fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'ngrok-skip-browser-warning': '1',
          },
          body: JSON.stringify({ token }),
        });
      } catch (e) {
        console.warn('[InkApi] logout proxy unreachable (non-fatal)', e);
      }
      this.setToken(null);
      return { data: { ok: true }, error: null };
    }

    // === Generic helpers — mọi Laravel /api/v1/* endpoint qua App Proxy ===
    // Path bắt đầu bằng / (vd '/printer'), tự ghép apiBase ('/apps/ink/api/v1').
    get(path, params) {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return this._fetch('GET', path + qs);
    }
    post(path, body) { return this._fetch('POST', path, body); }
    put(path, body) { return this._fetch('PUT', path, body); }
    patch(path, body) { return this._fetch('PATCH', path, body); }
    delete(path) { return this._fetch('DELETE', path); }

    // Tải binary (vd PDF 領収証) qua App Proxy + token header. Trả Blob.
    async fetchBlob(path) {
      // Cùng lý do như `_fetch`: 領収証 PDF là dữ liệu cá nhân, không gửi kèm token
      // mà tab này giữ lại từ customer cũ.
      this._syncTokenFromStorage();

      // Cùng luật chờ-exchange như `_fetch` — PDF là endpoint member, gửi không
      // token là chắc chắn 401.
      if (!this.token && this.shopifyCustomer && !this._identityStale) {
        await this.exchange();
        this._syncTokenFromStorage();
      }

      const headers = { 'ngrok-skip-browser-warning': '1' };
      if (this.token) headers['X-Ink-Token'] = this.token;
      const res = await fetch(this.apiBase + path, { headers, credentials: 'same-origin' });
      if (!res.ok) throw new Error('blob fetch failed: ' + res.status);
      return res.blob();
    }

    async _fetch(method, path, body = null, _isRetry = false) {
      this._syncTokenFromStorage();

      // Đã login mà chưa có token (lần đầu / vừa purge) → CHỜ exchange xong mới gửi
      // (join promise đang bay — gồm cả early-exchange từ <head>). Không chờ thì mọi
      // call member lúc boot ăn 401 rồi retry: mỗi call tốn thêm 1 roundtrip 4-hop
      // + gấp đôi tải Laravel. `_isRetry` không chờ lại — exchange vừa fail thì bắn
      // thêm lần nữa cũng vô ích.
      if (!this.token && this.shopifyCustomer && !this._identityStale && !_isRetry) {
        await this.exchange();
        this._syncTokenFromStorage();
      }

      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // Bypass ngrok free tier browser warning page (dev only — production
        // Shopify App Proxy đến URL khác sẽ ignore header này).
        'ngrok-skip-browser-warning': '1',
      };
      // Token qua CUSTOM header X-Ink-Token thay vì Authorization. Shopify
      // App Proxy có thể strip Authorization header, nhưng
      // custom header được preserve. Remix proxy.api.$ re-attach Authorization
      // khi forward đến Laravel.
      if (this.token) {
        headers['X-Ink-Token'] = this.token;
      }

      const options = { method, headers, credentials: 'same-origin' };
      if (body !== null && body !== undefined) {
        options.body = JSON.stringify(body);
      }

      // Mọi path qua catch-all proxy.api.$ route (/apps/ink/api/v1/*).
      // exchange + logout có route Remix riêng — gọi method chuyên biệt.
      const url = this.apiBase + path;

      // Adopt request bắn SỚM từ snippet `api-early-fetch` (inline trong section,
      // chạy ngay lúc parse HTML — perf §C‴). Chỉ nhận khi CÙNG URL + CÙNG token
      // (đổi account giữa chừng → bỏ, fetch mới); dùng đúng 1 lần. Response đi
      // tiếp pipeline thường bên dưới (401 re-exchange/204/empty-body) nên chốt
      // bảo mật + tự chữa giữ nguyên. Promise sớm lỗi mạng → fallback fetch mới.
      let earlyPromise = null;
      if (method === 'GET' && !_isRetry && window.__inkEarlyApi) {
        const early = window.__inkEarlyApi['GET ' + url];
        if (early && early.token === this.token) {
          delete window.__inkEarlyApi['GET ' + url];
          earlyPromise = early.promise;
        }
      }

      let response;
      try {
        response = earlyPromise
          ? await earlyPromise.catch(() => fetch(url, options))
          : await fetch(url, options);
      } catch (e) {
        console.error('[InkApi] network error', url, e);
        return { data: null, error: { code: 'NETWORK_ERROR', message: e.message } };
      }

      // 401 → token hết hạn / bị revoke. Clear + re-exchange + retry 1 lần.
      // Chỉ retry khi (a) customer đang login Shopify, (b) chưa retry, (c) identity của
      // tab này chưa cũ — nếu cũ thì exchange sẽ gửi customer_id cũ và Remix trả
      // CUSTOMER_MISMATCH, vô ích mà còn xoá token của phiên đang sống.
      if (response.status === 401 && !_isRetry && this.shopifyCustomer && !this._identityStale) {
        this._clearOwnToken();
        const exchanged = await this.exchange();
        if (exchanged?.data?.token) {
          return this._fetch(method, path, body, /* _isRetry */ true);
        }
      }

      // 204/205 = thành công KHÔNG body (DELETE printer/配達先/coupon). response.json()
      // throw trên body rỗng → nếu để rơi xuống nhánh dưới thì caller nhận
      // PARSE_ERROR và báo 削除に失敗しました dù server đã xoá xong.
      if (response.status === 204 || response.status === 205) {
        return { data: null };
      }

      // Status khác mà body vẫn RỖNG: 2xx là thành công thật, còn lỗi (502 proxy
      // timeout, 500 không body) PHẢI giữ nguyên là lỗi — nếu không caller tưởng
      // xong việc. Đọc text trước để response.json() không throw trên body rỗng.
      const text = await response.text().catch(() => '');
      if (text === '') {
        return response.ok
          ? { data: null }
          : { data: null, error: { code: 'HTTP_' + response.status, message: 'Empty error response' } };
      }

      try {
        return JSON.parse(text);
      } catch (e) {
        return { data: null, error: { code: 'PARSE_ERROR', message: 'Invalid JSON response' } };
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Purge lại: config có thể vừa đổi (SPA-ish navigation / bfcache restore) so
    // với lúc script load.
    purgeForeignToken(window.INK_API_CONFIG || {});

    const api = new InkApiClient(window.INK_API_CONFIG || {});
    window.inkApi = api;

    // Báo các page JS đang chờ (cart.js...) — api-client load CUỐI body
    // (theme.liquid sau content_for_layout) nên DCL listener của section JS
    // fire TRƯỚC listener này; KHÔNG thể dựa vào listener order.
    // Consumer chờ event này thay vì DOMContentLoaded.
    document.dispatchEvent(new CustomEvent('ink:api:ready'));

    // Nếu Shopify customer logged in nhưng chưa có Laravel token → exchange ngay
    // (không đợi user tương tác). Fail-silent — error chỉ log, không crash page.
    if (api.shopifyCustomer && !api.token) {
      api.exchange().catch((e) => console.error('[InkApi] auto-exchange failed', e));
    }

    // bfcache: back/forward KHÔNG chạy lại DOMContentLoaded, nên trang có thể sống
    // lại sau khi khách đã đổi account ở tab khác.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;

      // Trang bfcache: KIỂM identity TRƯỚC, purge SAU. Nếu purge trước thì ca "trang guest
      // được khôi phục sau khi khách login ở tab khác" sẽ đi vào nhánh guest và XOÁ token
      // đang sống của tab kia; xoá xong storage rỗng nên không còn dấu vết để nhận ra trang
      // này cũ ⇒ trang kẹt trạng thái guest và tab kia mất session.
      // Storage có chủ mà chủ đó ≠ identity trang này (kể cả trang này là guest) = bằng
      // chứng trang này cũ → reload để lấy `customer` mới từ Liquid, KHÔNG đụng storage.
      const storedOwner = localStorage.getItem(OWNER_KEY);
      if (storedOwner && storedOwner !== ownerOf(window.INK_API_CONFIG || {})) {
        api._identityStale = true;
        api.token = null;
        window.location.reload();

        return;
      }

      purgeForeignToken(window.INK_API_CONFIG || {});
      api._syncTokenFromStorage(/* detectStale */ true);

      // Identity của trang bfcache này đã cũ (khách login account khác ở tab/lần khác):
      // chỉ reload mới lấy được `customer` mới từ Liquid. Làm ở pageshow vì đây là thời
      // điểm an toàn — khách vừa quay lại trang, không có form nào đang gõ. KHÔNG reload
      // ở `storage` (tab khác đổi) vì có thể đang nhập 会員情報.
      if (api._identityStale) window.location.reload();
    });

    // Tab KHÁC đổi/xoá token (login account khác, logout) → tab này bỏ token cũ ngay
    // thay vì đợi request đầu tiên.
    window.addEventListener('storage', (e) => {
      if (e.key === TOKEN_KEY || e.key === OWNER_KEY) {
        // detectStale: tab khác vừa ghi ⇒ nếu chủ mới khác identity tab này thì CHÍNH tab
        // này đang giữ `customer` cũ (Liquid render từ lâu) → dừng dùng/exchange token.
        api._syncTokenFromStorage(/* detectStale */ true);
      }
    });
  });
})();
